import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { fmtUsd0 } from "../format";

export interface DollarRetentionData {
  /** Net Revenue Retention — Σ current / Σ prior-year, uncapped (expansion
   *  pushes it above 100%). Null when the base is empty. */
  nrrPct: number | null;
  /** Gross Dollar Retention — Σ min(current, prior-year) / Σ prior-year,
   *  floored per entity so it can never exceed 100%. */
  gdrPct: number | null;
  /** Σ prior-year MRR × 12 — the base 12 months ago. */
  baseArr: number;
  /** Σ current MRR × 12 — the NRR numerator (billed now). */
  currentArr: number;
  /** Σ min(current, prior-year) × 12 — the GDR numerator ("$-for-$ retained"). */
  retainedArr: number;
  baseCount: number;
  source?: string;
}

/** Color scale shared by NRR (uncapped) and GDR (≤100%): green ≥100, purple
 *  ≥85, red below. */
function tone(pct: number | null | undefined): string {
  if (pct == null) return "text-muted-foreground";
  if (pct >= 100) return "text-success";
  if (pct >= 85) return "text-purple";
  return "text-danger";
}

export function DollarRetentionCard({
  label,
  data,
}: {
  label: string;
  data: DollarRetentionData | null;
}) {
  const nrr = data?.nrrPct;
  const gdr = data?.gdrPct;
  return (
    <Card className="relative h-full overflow-hidden p-5">
      <div className="absolute left-0 top-0 h-full w-1 bg-purple" />
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </div>

      <div className="mt-2 flex items-end gap-5">
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
            NRR
          </div>
          <div className={cn("text-3xl font-extrabold leading-none tracking-tight tabular-nums", tone(nrr))}>
            {data ? (nrr != null ? `${nrr.toFixed(1)}%` : "—") : "—"}
          </div>
        </div>
        <div>
          <div className="text-[9px] font-bold uppercase tracking-wider text-muted-foreground/70">
            GDR
          </div>
          <div className={cn("text-xl font-extrabold leading-none tracking-tight tabular-nums", tone(gdr))}>
            {data ? (gdr != null ? `${gdr.toFixed(1)}%` : "—") : "—"}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-col gap-1.5">
        <Row label="Cohort ARR · 12 mo prior" value={data ? fmtUsd0(data.baseArr) : null} />
        <Row
          label="$-for-$ retained (GDR)"
          value={data ? fmtUsd0(data.retainedArr) : null}
          muted
        />
        <Row label="Billed now (NRR)" value={data ? fmtUsd0(data.currentArr) : null} />
        <div className="mt-1 flex items-baseline justify-between border-t border-border pt-2 text-xs">
          <span className="text-muted-foreground">Accounts in base</span>
          <span className="font-semibold tabular-nums">{data ? data.baseCount : "—"}</span>
        </div>
      </div>

      <div className="mt-3 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground/50">
        {data?.source ?? "Revenue waterfall · YoY current vs 12 mo prior"}
      </div>
    </Card>
  );
}

function Row({
  label,
  value,
  muted = false,
}: {
  label: string;
  value: string | null;
  muted?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between text-xs">
      <span className={cn(muted ? "text-muted-foreground/70" : "text-muted-foreground")}>
        {label}
      </span>
      <span className={cn("font-semibold tabular-nums", muted && "text-muted-foreground")}>
        {value ?? "—"}
      </span>
    </div>
  );
}
